import { computed, toValue, watch } from "vue";

import { useHostElement } from "#ce/composables/useHostElement";

// ARIA element reflection, the only way a control in a shadow root can reference
// a labelling element outside its own tree.
const SUPPORTS_ELEMENT_REFLECTION = "ariaLabelledByElements" in Element.prototype;

/**
 * Names a control rendered inside the shadow root from the host element's ARIA attributes.
 *
 * A host `aria-label` never reaches a descendant in the shadow root, and an
 * `aria-labelledby` IDREF does not resolve across the shadow boundary. The referenced
 * elements are resolved against the host's own tree and attached to the target through
 * ARIA element reflection; where reflection is unsupported their text is used as the
 * target's `aria-label` instead.
 *
 * @param {import('vue').MaybeRefOrGetter<Element|null|undefined>} target - Control to name.
 * @param {object} sources
 * @param {import('vue').MaybeRefOrGetter<string|undefined>} sources.label - The host's `aria-label`.
 * @param {import('vue').MaybeRefOrGetter<string|undefined>} sources.labelledby - The host's `aria-labelledby`.
 * @param {import('vue').MaybeRefOrGetter<Element[]|undefined>} [sources.elements] - Elements labelling the
 *   host by other means, such as the `<label>` elements of a form-associated host. Used only when the
 *   host carries neither `aria-labelledby` nor `aria-label`, the order the accessible name is computed in.
 * @returns {{ ariaLabel: import('vue').ComputedRef<string|undefined> }} The `aria-label` to bind on the target.
 */
export function useAriaLabel(target, { label, labelledby, elements }) {
  const host = useHostElement();

  const referencedElements = computed(() => {
    const ids = toValue(labelledby)?.trim();

    if (!ids || !host) {
      return [];
    }

    const root = host.getRootNode();

    return ids
      .split(/\s+/)
      .map((id) => root.getElementById?.(id))
      .filter(Boolean);
  });

  const labellingElements = computed(() => {
    if (referencedElements.value.length > 0) {
      return referencedElements.value;
    }

    return toValue(label) ? [] : (toValue(elements) ?? []);
  });

  const labellingText = computed(() => {
    const text = labellingElements.value
      .map((element) => element.textContent?.trim())
      .filter(Boolean)
      .join(" ");

    return text || undefined;
  });

  const ariaLabel = computed(() => {
    if (SUPPORTS_ELEMENT_REFLECTION) {
      return toValue(label);
    }

    // aria-labelledby outranks aria-label in the accessible name computation.
    return labellingText.value ?? toValue(label);
  });

  watch(
    [() => toValue(target), labellingElements],
    ([element, elements]) => {
      if (!SUPPORTS_ELEMENT_REFLECTION || !element) {
        return;
      }

      element.ariaLabelledByElements = elements.length > 0 ? elements : null;
    },
    { immediate: true, flush: "post" },
  );

  return { ariaLabel };
}
